import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk, ArrowsClockwise, UploadSimple, DownloadSimple } from '@phosphor-icons/react';

const TABS = [
  { key: 'categories', label: 'Categories' },
  { key: 'uqc', label: 'UQC' },
  { key: 'profiles', label: 'Profiles' },
  { key: 'colors', label: 'Colors' },
  { key: 'materials', label: 'Materials' },
  { key: 'items', label: 'Items' },
  { key: 'bom', label: 'BOM' },
  { key: 'itemmap', label: 'Item-Material Map' },
];

/* ═══════ EXCEL UPLOAD HELPER ═══════ */
function ExcelUploadButton({ endpoint, onSuccess }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`${endpoint}/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message || `Imported ${data.count} entries`);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = async () => {
    const parts = endpoint.split('/');
    const masterType = parts[parts.length - 1];
    try {
      const { data } = await api.get(`/api/inventory/masters/${masterType}/template`);
      const csv = data.columns.join(',') + '\n';
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${masterType}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  return (
    <div className="flex gap-2 items-center">
      <label className="cursor-pointer">
        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} className="hidden" />
        <span data-testid="master-upload-excel" className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold text-white ${uploading ? 'bg-amber-400' : 'bg-amber-600 hover:bg-amber-700'} cursor-pointer`}>
          <UploadSimple size={14} /> {uploading ? 'Importing...' : 'Upload Excel'}
        </span>
      </label>
      <button
        data-testid="master-download-template"
        onClick={downloadTemplate}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold text-zinc-600 border border-zinc-300 hover:bg-zinc-100 cursor-pointer"
      >
        <DownloadSimple size={14} /> Template
      </button>
    </div>
  );
}

/* ═══════ GENERIC SIMPLE MASTER (Category, UQC, Profile) ═══════ */
function SimpleMaster({ endpoint, fields, keyField }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const rowIdRef = { current: 0 };

  const fetch = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      setRows(data.map(d => ({ ...d, _rowId: `srv-${rowIdRef.current++}` })));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { fetch(); }, [fetch]);

  const addRow = () => {
    const empty = { _rowId: `new-${rowIdRef.current++}` };
    fields.forEach(f => { empty[f.key] = ''; });
    setRows([...rows, empty]);
  };

  const updateRow = (idx, key, val) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: val };
    setRows(copy);
  };

  const deleteRow = async (idx) => {
    const row = rows[idx];
    const id = row[keyField];
    if (id && !id.startsWith?.('__new')) {
      // Check usage before delete
      const masterType = endpoint.split('/').pop(); // categories, uqc, profiles, colors
      try {
        const { data: usage } = await api.get(`/api/inventory/masters/check-usage/${masterType}/${encodeURIComponent(id)}`);
        if (usage.in_use) {
          toast.error(`Cannot delete "${id}" — used in: ${usage.used_in.join(', ')}`);
          return;
        }
      } catch (err) { console.warn(`Usage check failed for ${masterType}/${id}:`, err?.message); }
      try {
        await api.delete(`${endpoint}/${encodeURIComponent(id)}`);
        toast.success('Deleted');
      } catch { toast.error('Delete failed'); return; }
    }
    setRows(rows.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      const valid = rows.filter(r => r[keyField]?.trim());
      // Client-side duplicate check (case-insensitive)
      const seen = new Map();
      for (const r of valid) {
        const norm = String(r[keyField]).trim().toLowerCase();
        if (seen.has(norm)) {
          toast.error(`Duplicate master already exists: "${r[keyField]}"`);
          setSaving(false);
          return;
        }
        seen.set(norm, true);
      }
      const { data } = await api.post(`${endpoint}/bulk`, valid);
      toast.success(data?.message || 'Saved');
      fetch();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    finally { setSaving(false); }
  };

  if (loading) return <p className="p-4 text-zinc-500">Loading...</p>;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <Button data-testid="master-add-row" onClick={addRow} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={14} className="mr-1" /> Add Row
        </Button>
        <Button data-testid="master-save" onClick={save} size="sm" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <FloppyDisk size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save All'}
        </Button>
        <Button onClick={fetch} size="sm" variant="outline"><ArrowsClockwise size={14} /></Button>
        <div className="flex-1" />
        <ExcelUploadButton endpoint={endpoint} onSuccess={fetch} />
      </div>
      <div className="overflow-x-auto border rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b">
              {fields.map(f => <th key={f.key} className="text-left p-2 font-bold text-xs uppercase tracking-wider text-zinc-500">{f.label}</th>)}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row._rowId} className="border-b hover:bg-zinc-50">
                {fields.map(f => (
                  <td key={f.key} className="p-1.5">
                    <Input
                      data-testid={`master-${f.key}-${idx}`}
                      value={row[f.key] || ''}
                      onChange={e => updateRow(idx, f.key, e.target.value)}
                      className="h-8 text-sm"
                      placeholder={f.label}
                    />
                  </td>
                ))}
                <td className="p-1.5">
                  <Button variant="ghost" size="sm" onClick={() => deleteRow(idx)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700">
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={fields.length + 1} className="p-6 text-center text-zinc-400">No entries. Click "Add Row" to start.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════ MATERIAL MASTER ═══════ */
function MaterialMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uqcs, setUqcs] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [colors, setColors] = useState([]);

  const rowIdRef = { current: 0 };

  const fetchAll = useCallback(async () => {
    try {
      const [matRes, catRes, uqcRes, profRes, colRes] = await Promise.all([
        api.get('/api/inventory/masters/materials'),
        api.get('/api/inventory/masters/categories'),
        api.get('/api/inventory/masters/uqc'),
        api.get('/api/inventory/masters/profiles'),
        api.get('/api/inventory/masters/colors'),
      ]);
      setRows(matRes.data.map(d => ({ ...d, _rowId: `srv-${rowIdRef.current++}` })));
      setCategories(catRes.data.map(c => c.name));
      setUqcs(uqcRes.data.map(u => u.code));
      setProfiles(profRes.data.map(p => p.code));
      setColors(colRes.data.map(c => c.name));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load materials');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addRow = () => setRows([...rows, { _rowId: `new-${rowIdRef.current++}`, material_name: '', material_code: '', category: '', profile: '', color_type: 'white', uqc: '', standard_length: 0, opening_stock: 0, min_stock: 0, reorder_level: 0, current_stock: 0, reserved_stock: 0 }]);

  const updateRow = (idx, key, val) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: val };
    setRows(copy);
  };

  const deleteRow = async (idx) => {
    const name = rows[idx]?.material_name;
    if (name) {
      try {
        const { data: usage } = await api.get(`/api/inventory/masters/check-usage/materials/${encodeURIComponent(name)}`);
        if (usage.in_use) {
          toast.error(`Cannot delete "${name}" — used in: ${usage.used_in.join(', ')}`);
          return;
        }
      } catch (err) { console.warn(`Usage check failed for materials/${name}:`, err?.message); }
      try { await api.delete(`/api/inventory/masters/materials/${encodeURIComponent(name)}`); toast.success('Deleted'); }
      catch { toast.error('Delete failed'); return; }
    }
    setRows(rows.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      const valid = rows.filter(r => r.material_name?.trim());
      // Client-side duplicate check (case-insensitive)
      const seen = new Map();
      for (const r of valid) {
        const norm = String(r.material_name).trim().toLowerCase();
        if (seen.has(norm)) {
          toast.error(`Duplicate master already exists: "${r.material_name}"`);
          setSaving(false);
          return;
        }
        seen.set(norm, true);
      }
      const { data } = await api.post('/api/inventory/masters/materials/bulk', valid);
      toast.success(data?.message || 'Materials saved');
      fetchAll();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    finally { setSaving(false); }
  };

  if (loading) return <p className="p-4 text-zinc-500">Loading materials...</p>;

  const FIELDS = [
    { key: 'material_name', label: 'Material Name', type: 'text' },
    { key: 'material_code', label: 'Code', type: 'text' },
    { key: 'category', label: 'Category', type: 'select', options: categories },
    { key: 'profile', label: 'Profile', type: 'select', options: profiles },
    { key: 'color_type', label: 'Color', type: 'select', options: ['white', ...colors] },
    { key: 'uqc', label: 'UQC', type: 'select', options: uqcs },
    { key: 'standard_length', label: 'Std Length', type: 'number' },
    { key: 'opening_stock', label: 'Opening Stock', type: 'number' },
    { key: 'min_stock', label: 'Min Stock', type: 'number' },
    { key: 'reorder_level', label: 'Reorder Level', type: 'number' },
    { key: 'current_stock', label: 'Current Stock', type: 'number' },
    { key: 'reserved_stock', label: 'Reserved Stock', type: 'number' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <Button onClick={addRow} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={14} className="mr-1" /> Add Material
        </Button>
        <Button onClick={save} size="sm" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <FloppyDisk size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save All'}
        </Button>
        <Button onClick={fetchAll} size="sm" variant="outline"><ArrowsClockwise size={14} /></Button>
        <div className="flex-1" />
        <ExcelUploadButton endpoint="/api/inventory/masters/materials" onSuccess={fetchAll} />
      </div>
      <div className="overflow-x-auto border rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b">
              {FIELDS.map(f => <th key={f.key} className="text-left p-2 font-bold text-xs uppercase tracking-wider text-zinc-500 whitespace-nowrap">{f.label}</th>)}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row._rowId || `mat-${idx}`} className="border-b hover:bg-zinc-50">
                {FIELDS.map(f => (
                  <td key={f.key} className="p-1">
                    {f.type === 'select' ? (
                      <select
                        data-testid={`mat-${f.key}-${idx}`}
                        value={row[f.key] || ''}
                        onChange={e => updateRow(idx, f.key, e.target.value)}
                        className="h-8 text-sm border rounded px-2 w-full bg-white"
                      >
                        <option value="">--</option>
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <Input
                        data-testid={`mat-${f.key}-${idx}`}
                        type={f.type}
                        value={row[f.key] ?? ''}
                        onChange={e => updateRow(idx, f.key, f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
                        className="h-8 text-sm"
                        placeholder={f.label}
                      />
                    )}
                  </td>
                ))}
                <td className="p-1">
                  <Button variant="ghost" size="sm" onClick={() => deleteRow(idx)} className="h-8 w-8 p-0 text-red-500">
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={FIELDS.length + 1} className="p-6 text-center text-zinc-400">No materials.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════ ITEM MASTER ═══════ */
function ItemMaster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState([]);

  const rowIdRef = { current: 0 };

  const fetchAll = useCallback(async () => {
    try {
      const [itemRes, profRes] = await Promise.all([
        api.get('/api/inventory/masters/items'),
        api.get('/api/inventory/masters/profiles'),
      ]);
      setRows(itemRes.data.map(d => ({ ...d, _rowId: `srv-${rowIdRef.current++}` })));
      setProfiles(profRes.data.map(p => p.code));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addRow = () => setRows([...rows, { _rowId: `new-${rowIdRef.current++}`, item_name: '', profile_mapping: '', item_type: '', mesh_applicable: false, glass_applicable: false, color_applicable: false }]);

  const updateRow = (idx, key, val) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: val };
    setRows(copy);
  };

  const deleteRow = async (idx) => {
    const name = rows[idx]?.item_name;
    if (name) {
      try {
        const { data: usage } = await api.get(`/api/inventory/masters/check-usage/items/${encodeURIComponent(name)}`);
        if (usage.in_use) {
          toast.error(`Cannot delete "${name}" — used in: ${usage.used_in.join(', ')}`);
          return;
        }
      } catch (err) { console.warn(`Usage check failed for items/${name}:`, err?.message); }
      try { await api.delete(`/api/inventory/masters/items/${encodeURIComponent(name)}`); }
      catch { toast.error('Delete failed'); return; }
    }
    setRows(rows.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      const valid = rows.filter(r => r.item_name?.trim());
      const seen = new Map();
      for (const r of valid) {
        const norm = String(r.item_name).trim().toLowerCase();
        if (seen.has(norm)) {
          toast.error(`Duplicate master already exists: "${r.item_name}"`);
          setSaving(false);
          return;
        }
        seen.set(norm, true);
      }
      const { data } = await api.post('/api/inventory/masters/items/bulk', valid);
      toast.success(data?.message || 'Items saved');
      fetchAll();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    finally { setSaving(false); }
  };

  if (loading) return <p className="p-4 text-zinc-500">Loading...</p>;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <Button onClick={addRow} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus size={14} className="mr-1" /> Add Item</Button>
        <Button onClick={save} size="sm" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white"><FloppyDisk size={14} className="mr-1" /> Save All</Button>
        <Button onClick={fetchAll} size="sm" variant="outline"><ArrowsClockwise size={14} /></Button>
        <div className="flex-1" />
        <ExcelUploadButton endpoint="/api/inventory/masters/items" onSuccess={fetchAll} />
      </div>
      <div className="overflow-x-auto border rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b">
              <th className="text-left p-2 font-bold text-xs uppercase tracking-wider text-zinc-500">Item Name</th>
              <th className="text-left p-2 font-bold text-xs uppercase tracking-wider text-zinc-500">Profile</th>
              <th className="text-left p-2 font-bold text-xs uppercase tracking-wider text-zinc-500">Item Type</th>
              <th className="text-center p-2 font-bold text-xs uppercase tracking-wider text-zinc-500">Mesh</th>
              <th className="text-center p-2 font-bold text-xs uppercase tracking-wider text-zinc-500">Glass</th>
              <th className="text-center p-2 font-bold text-xs uppercase tracking-wider text-zinc-500">Color</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row._rowId || `item-${idx}`} className="border-b hover:bg-zinc-50">
                <td className="p-1"><Input value={row.item_name || ''} onChange={e => updateRow(idx, 'item_name', e.target.value)} className="h-8 text-sm" /></td>
                <td className="p-1">
                  <select value={row.profile_mapping || ''} onChange={e => updateRow(idx, 'profile_mapping', e.target.value)} className="h-8 text-sm border rounded px-2 w-full bg-white">
                    <option value="">--</option>
                    {profiles.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td className="p-1"><Input value={row.item_type || ''} onChange={e => updateRow(idx, 'item_type', e.target.value)} className="h-8 text-sm" /></td>
                <td className="p-1 text-center"><input type="checkbox" checked={row.mesh_applicable || false} onChange={e => updateRow(idx, 'mesh_applicable', e.target.checked)} /></td>
                <td className="p-1 text-center"><input type="checkbox" checked={row.glass_applicable || false} onChange={e => updateRow(idx, 'glass_applicable', e.target.checked)} /></td>
                <td className="p-1 text-center"><input type="checkbox" checked={row.color_applicable || false} onChange={e => updateRow(idx, 'color_applicable', e.target.checked)} /></td>
                <td className="p-1"><Button variant="ghost" size="sm" onClick={() => deleteRow(idx)} className="h-8 w-8 p-0 text-red-500"><Trash size={14} /></Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No items.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════ BOM MASTER (Grouped: Finished Good → Raw Materials) ═══════ */
function BOMMaster() {
  const [groups, setGroups] = useState({});  // { itemName: [rawMaterials] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [uqcs, setUqcs] = useState([]);
  const [expanded, setExpanded] = useState(null);  // currently expanded item
  const [newItemName, setNewItemName] = useState('');

  const CONSUMPTION_TYPES = ['length', 'area', 'quantity'];
  const CATEGORIES = ['Frame', 'Sash', 'Bead', 'Reinforcement', 'Glass', 'Mesh', 'Hardware', 'Accessories', 'Gasket', 'Sealant'];

  const fetchAll = useCallback(async () => {
    try {
      const [bomRes, itemRes, matRes, uqcRes] = await Promise.all([
        api.get('/api/inventory/masters/bom'),
        api.get('/api/inventory/masters/items'),
        api.get('/api/inventory/masters/materials'),
        api.get('/api/inventory/masters/uqc'),
      ]);
      // Group BOM rows by item_name
      const grouped = {};
      for (const row of bomRes.data) {
        const key = row.item_name || '';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
      }
      setGroups(grouped);
      setItems(itemRes.data.map(i => i.item_name));
      setMaterials(matRes.data.map(m => m.material_name));
      setUqcs(uqcRes.data.map(u => u.code));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load BOM');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addFinishedGood = () => {
    if (!newItemName) { toast.error('Select a Finished Item first'); return; }
    if (groups[newItemName]) { toast.error('This item already has a BOM. Click to expand it.'); return; }
    setGroups({ ...groups, [newItemName]: [] });
    setExpanded(newItemName);
    setNewItemName('');
  };

  const addRawMaterial = (itemName) => {
    const copy = { ...groups };
    copy[itemName] = [...(copy[itemName] || []), {
      item_name: itemName, material_name: '', category: '', consumption_type: 'length',
      consumption_factor: 1, wastage_pct: 0, uqc: '', formula_type: ''
    }];
    setGroups(copy);
  };

  const updateRawMaterial = (itemName, rmIdx, key, val) => {
    const copy = { ...groups };
    const mats = [...copy[itemName]];
    mats[rmIdx] = { ...mats[rmIdx], [key]: val };
    copy[itemName] = mats;
    setGroups(copy);
  };

  const removeRawMaterial = async (itemName, rmIdx) => {
    const row = groups[itemName]?.[rmIdx];
    if (row?.item_name && row?.material_name) {
      try { await api.delete(`/api/inventory/masters/bom?item_name=${encodeURIComponent(row.item_name)}&material_name=${encodeURIComponent(row.material_name)}`); }
      catch (err) { console.warn(`BOM row delete skipped (may be unsaved): ${row.item_name}/${row.material_name}`, err?.message); }
    }
    const copy = { ...groups };
    copy[itemName] = copy[itemName].filter((_, i) => i !== rmIdx);
    if (copy[itemName].length === 0) delete copy[itemName];
    setGroups(copy);
  };

  const removeFinishedGood = async (itemName) => {
    if (!window.confirm(`Remove BOM for "${itemName}" and all its raw materials?`)) return;
    for (const row of (groups[itemName] || [])) {
      if (row.material_name) {
        try { await api.delete(`/api/inventory/masters/bom?item_name=${encodeURIComponent(itemName)}&material_name=${encodeURIComponent(row.material_name)}`); }
        catch (err) { console.warn(`Batch BOM delete failed for ${itemName}/${row.material_name}:`, err?.message); }
      }
    }
    const copy = { ...groups };
    delete copy[itemName];
    setGroups(copy);
    if (expanded === itemName) setExpanded(null);
    toast.success(`BOM for "${itemName}" removed`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const allRows = Object.values(groups).flat().filter(r => r.item_name && r.material_name);
      // Dedup check on (item_name, material_name) pair — case-insensitive
      const seen = new Map();
      for (const r of allRows) {
        const norm = `${String(r.item_name).trim().toLowerCase()}|${String(r.material_name).trim().toLowerCase()}`;
        if (seen.has(norm)) {
          toast.error(`Duplicate master already exists: ${r.item_name} / ${r.material_name}`);
          setSaving(false);
          return;
        }
        seen.set(norm, true);
      }
      const { data } = await api.post('/api/inventory/masters/bom/bulk', allRows);
      toast.success(data?.message || `Saved ${allRows.length} BOM entries`);
      fetchAll();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
    finally { setSaving(false); }
  };

  const itemNames = useMemo(() => Object.keys(groups).sort(), [groups]);
  const availableItems = useMemo(() => items.filter(i => !groups[i]), [items, groups]);

  if (loading) return <p className="p-4 text-zinc-500">Loading...</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">Select a <strong>Finished Item</strong>, then add <strong>Raw Materials</strong> inside it.</p>

      {/* Add New Finished Good */}
      <div className="flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-bold text-zinc-500 uppercase">Add Finished Item</label>
          <select
            data-testid="bom-add-fg-select"
            value={newItemName}
            onChange={e => setNewItemName(e.target.value)}
            className="h-8 mt-1 text-xs border rounded px-2 w-full bg-white"
          >
            <option value="">-- Select Finished Item --</option>
            {availableItems.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <Button data-testid="bom-add-fg-btn" onClick={addFinishedGood} size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={14} className="mr-1" /> Add Finished Good
        </Button>
        <Button onClick={save} size="sm" disabled={saving} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
          <FloppyDisk size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save All'}
        </Button>
        <Button onClick={fetchAll} size="sm" variant="outline" className="h-8"><ArrowsClockwise size={14} /></Button>
        <ExcelUploadButton endpoint="/api/inventory/masters/bom" onSuccess={fetchAll} />
      </div>

      {/* Finished Goods List */}
      {itemNames.length === 0 && (
        <div className="p-8 text-center text-zinc-400 border rounded bg-zinc-50">
          No BOM defined. Select a Finished Item above and add raw materials.
        </div>
      )}
      <div className="space-y-2">
        {itemNames.map(itemName => {
          const mats = groups[itemName] || [];
          const isExpanded = expanded === itemName;
          return (
            <div key={itemName} className={`border rounded ${isExpanded ? 'border-orange-300 bg-orange-50/30' : 'border-zinc-200'}`}>
              {/* Finished Good Header */}
              <div
                className="flex items-center gap-2 p-2.5 cursor-pointer hover:bg-zinc-50"
                onClick={() => setExpanded(isExpanded ? null : itemName)}
              >
                <span className={`transition-transform text-zinc-400 ${isExpanded ? 'rotate-90' : ''}`}>&#9654;</span>
                <span className="font-bold text-sm text-zinc-900 flex-1">{itemName}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">{mats.length} material{mats.length !== 1 ? 's' : ''}</span>
                <Button
                  variant="ghost" size="sm"
                  onClick={e => { e.stopPropagation(); removeFinishedGood(itemName); }}
                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                >
                  <Trash size={12} />
                </Button>
              </div>

              {/* Raw Materials (expanded) */}
              {isExpanded && (
                <div className="border-t px-3 pb-3 pt-2 bg-white">
                  <div className="flex gap-2 mb-2">
                    <Button
                      data-testid={`bom-add-rm-${itemName}`}
                      onClick={() => addRawMaterial(itemName)}
                      size="sm"
                      className="h-7 bg-orange-600 hover:bg-orange-700 text-white text-xs"
                    >
                      <Plus size={12} className="mr-1" /> Add Raw Material
                    </Button>
                  </div>
                  {mats.length === 0 ? (
                    <p className="text-xs text-zinc-400 py-2">No raw materials added. Click "Add Raw Material" above.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-zinc-50 border-b">
                            <th className="text-left p-1.5 font-bold uppercase text-zinc-500">Raw Material</th>
                            <th className="text-left p-1.5 font-bold uppercase text-zinc-500">Category</th>
                            <th className="text-left p-1.5 font-bold uppercase text-zinc-500">Consumption</th>
                            <th className="text-left p-1.5 font-bold uppercase text-zinc-500 w-16">Factor</th>
                            <th className="text-left p-1.5 font-bold uppercase text-zinc-500 w-16">Wastage%</th>
                            <th className="text-left p-1.5 font-bold uppercase text-zinc-500 w-16">UQC</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {mats.map((rm, rmIdx) => (
                            <tr key={rm.material_name || `new-${rmIdx}`} className="border-b hover:bg-zinc-50">
                              <td className="p-1">
                                <select value={rm.material_name || ''} onChange={e => updateRawMaterial(itemName, rmIdx, 'material_name', e.target.value)} className="h-7 text-xs border rounded px-1 w-full bg-white">
                                  <option value="">-- Select --</option>
                                  {materials.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                              </td>
                              <td className="p-1">
                                <select value={rm.category || ''} onChange={e => updateRawMaterial(itemName, rmIdx, 'category', e.target.value)} className="h-7 text-xs border rounded px-1 w-full bg-white">
                                  <option value="">--</option>
                                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td className="p-1">
                                <select value={rm.consumption_type || 'length'} onChange={e => updateRawMaterial(itemName, rmIdx, 'consumption_type', e.target.value)} className="h-7 text-xs border rounded px-1 w-full bg-white">
                                  {CONSUMPTION_TYPES.map(t => <option key={t} value={t}>{t} based</option>)}
                                </select>
                              </td>
                              <td className="p-1"><Input type="number" value={rm.consumption_factor ?? 1} onChange={e => updateRawMaterial(itemName, rmIdx, 'consumption_factor', parseFloat(e.target.value) || 1)} className="h-7 text-xs w-16" /></td>
                              <td className="p-1"><Input type="number" value={rm.wastage_pct ?? 0} onChange={e => updateRawMaterial(itemName, rmIdx, 'wastage_pct', parseFloat(e.target.value) || 0)} className="h-7 text-xs w-16" /></td>
                              <td className="p-1">
                                <select value={rm.uqc || ''} onChange={e => updateRawMaterial(itemName, rmIdx, 'uqc', e.target.value)} className="h-7 text-xs border rounded px-1 w-full bg-white">
                                  <option value="">--</option>
                                  {uqcs.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                              </td>
                              <td className="p-1">
                                <Button variant="ghost" size="sm" onClick={() => removeRawMaterial(itemName, rmIdx)} className="h-7 w-7 p-0 text-red-500"><Trash size={14} /></Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════ ITEM-MATERIAL MAP (with Color Variants) ═══════ */
function ItemMaterialMap() {
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});

  const fetchMap = useCallback(async () => {
    try {
      const [mapRes, matRes] = await Promise.all([
        api.get('/api/inventory/item-material-map'),
        api.get('/api/inventory/masters/materials'),
      ]);
      setRows(mapRes.data);
      setMaterials(matRes.data.map(m => m.material_name));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMap(); }, [fetchMap]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/api/inventory/item-material-map/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message);
      fetchMap();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all item-material mappings?')) return;
    try {
      await api.delete('/api/inventory/item-material-map');
      toast.success('Cleared');
      fetchMap();
    } catch { toast.error('Clear failed'); }
  };

  const startEdit = (row) => {
    setEditingRow(row.item_name);
    setEditData({
      frame_material_color: row.frame_material_color || '',
      sash_material_color: row.sash_material_color || '',
      bead_material_color: row.bead_material_color || '',
    });
  };

  const saveEdit = async (itemName) => {
    try {
      await api.put(`/api/inventory/item-material-map/${encodeURIComponent(itemName)}`, editData);
      toast.success('Color materials updated');
      setEditingRow(null);
      fetchMap();
    } catch (err) { toast.error('Save failed'); }
  };

  if (loading) return <p className="p-4 text-zinc-500">Loading...</p>;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <label className="cursor-pointer">
          <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
          <span data-testid="upload-item-map" className={`inline-flex items-center gap-1 px-4 py-1.5 rounded text-sm font-bold text-white ${uploading ? 'bg-amber-400' : 'bg-amber-600 hover:bg-amber-700'} cursor-pointer`}>
            <UploadSimple size={14} /> {uploading ? 'Importing...' : 'Upload Item-Material Excel'}
          </span>
        </label>
        <Button onClick={fetchMap} size="sm" variant="outline"><ArrowsClockwise size={14} /></Button>
        {rows.length > 0 && (
          <Button onClick={clearAll} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">
            <Trash size={14} className="mr-1" /> Clear All
          </Button>
        )}
        <span className="text-xs text-zinc-500 ml-2">{rows.length} mappings loaded</span>
      </div>
      <p className="text-xs text-zinc-500">
        <strong>White columns</strong> = default (white profile) materials. <strong>Color columns</strong> = materials used when item has a color profile (Mahogany, Golden Oak, etc.). Click edit to set color-variant materials.
      </p>
      {rows.length > 0 && (
        <div className="overflow-x-auto border rounded max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-zinc-50 z-10">
              <tr className="border-b">
                <th className="p-1.5 text-left font-bold uppercase text-zinc-500">Item Name</th>
                <th className="p-1.5 text-left font-bold uppercase text-zinc-500" colSpan={2}>
                  <span className="bg-zinc-200 px-1.5 py-0.5 rounded">Frame</span>
                </th>
                <th className="p-1.5 text-left font-bold uppercase text-zinc-500" colSpan={2}>
                  <span className="bg-zinc-200 px-1.5 py-0.5 rounded">Sash</span>
                </th>
                <th className="p-1.5 text-left font-bold uppercase text-zinc-500" colSpan={2}>
                  <span className="bg-zinc-200 px-1.5 py-0.5 rounded">Bead</span>
                </th>
                <th className="p-1.5 w-16"></th>
              </tr>
              <tr className="border-b bg-zinc-100">
                <th className="p-1 text-left text-zinc-400 font-normal"></th>
                <th className="p-1 text-left text-zinc-500 font-bold">White</th>
                <th className="p-1 text-left text-orange-600 font-bold">Color</th>
                <th className="p-1 text-left text-zinc-500 font-bold">White</th>
                <th className="p-1 text-left text-orange-600 font-bold">Color</th>
                <th className="p-1 text-left text-zinc-500 font-bold">White</th>
                <th className="p-1 text-left text-orange-600 font-bold">Color</th>
                <th className="p-1"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isEditing = editingRow === r.item_name;
                return (
                  <tr key={r.item_name} className="border-b hover:bg-zinc-50">
                    <td className="p-1.5 font-bold text-zinc-800">{r.item_name}</td>
                    <td className="p-1.5 text-blue-700">{r.frame_material || '--'}</td>
                    <td className="p-1.5">
                      {isEditing ? (
                        <select value={editData.frame_material_color} onChange={e => setEditData({...editData, frame_material_color: e.target.value})} className="h-6 text-xs border rounded px-1 w-full bg-white">
                          <option value="">-- Same as white --</option>
                          {materials.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      ) : (
                        <span className={r.frame_material_color ? 'text-orange-600 font-bold' : 'text-zinc-300'}>{r.frame_material_color || '--'}</span>
                      )}
                    </td>
                    <td className="p-1.5 text-purple-700">{r.sash_material || '--'}</td>
                    <td className="p-1.5">
                      {isEditing ? (
                        <select value={editData.sash_material_color} onChange={e => setEditData({...editData, sash_material_color: e.target.value})} className="h-6 text-xs border rounded px-1 w-full bg-white">
                          <option value="">-- Same as white --</option>
                          {materials.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      ) : (
                        <span className={r.sash_material_color ? 'text-orange-600 font-bold' : 'text-zinc-300'}>{r.sash_material_color || '--'}</span>
                      )}
                    </td>
                    <td className="p-1.5 text-emerald-700">{r.bead_material || '--'}</td>
                    <td className="p-1.5">
                      {isEditing ? (
                        <select value={editData.bead_material_color} onChange={e => setEditData({...editData, bead_material_color: e.target.value})} className="h-6 text-xs border rounded px-1 w-full bg-white">
                          <option value="">-- Same as white --</option>
                          {materials.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      ) : (
                        <span className={r.bead_material_color ? 'text-orange-600 font-bold' : 'text-zinc-300'}>{r.bead_material_color || '--'}</span>
                      )}
                    </td>
                    <td className="p-1.5 text-center">
                      {isEditing ? (
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => saveEdit(r.item_name)} className="h-6 px-2 text-xs bg-emerald-600 text-white">Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingRow(null)} className="h-6 px-2 text-xs">X</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => startEdit(r)} className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800">Edit</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CATEGORY_FIELDS = [{ key: 'name', label: 'Category Name' }, { key: 'description', label: 'Description' }];
const UQC_FIELDS = [{ key: 'code', label: 'UQC Code' }, { key: 'description', label: 'Description' }];
const PROFILE_FIELDS = [{ key: 'code', label: 'Profile Code' }, { key: 'name', label: 'Name' }, { key: 'description', label: 'Description' }];
const COLOR_FIELDS = [{ key: 'name', label: 'Color Name' }, { key: 'code', label: 'Code' }, { key: 'description', label: 'Description' }];

/* ═══════ MAIN MASTERS PAGE ═══════ */
export function InventoryMasters() {
  const [activeTab, setActiveTab] = useState('categories');

  return (
    <Layout>
      <div className="space-y-4 pb-10">
        <h1 className="text-4xl font-light tracking-tight text-zinc-950">Inventory Masters</h1>
        <p className="text-sm text-zinc-500">Manage categories, UQC, profiles, materials, items, and BOM</p>

        <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.key}
              data-testid={`master-tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-bold whitespace-nowrap transition-colors
                ${activeTab === tab.key
                  ? 'border-b-2 border-orange-600 text-orange-700 bg-orange-50/50'
                  : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-50'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <Card className="rounded-sm shadow-sm">
          <CardContent className="p-4">
            {activeTab === 'categories' && (
              <SimpleMaster endpoint="/api/inventory/masters/categories" fields={CATEGORY_FIELDS} keyField="name" />
            )}
            {activeTab === 'uqc' && (
              <SimpleMaster endpoint="/api/inventory/masters/uqc" fields={UQC_FIELDS} keyField="code" />
            )}
            {activeTab === 'profiles' && (
              <SimpleMaster endpoint="/api/inventory/masters/profiles" fields={PROFILE_FIELDS} keyField="code" />
            )}
            {activeTab === 'colors' && (
              <SimpleMaster endpoint="/api/inventory/masters/colors" fields={COLOR_FIELDS} keyField="name" />
            )}
            {activeTab === 'materials' && <MaterialMaster />}
            {activeTab === 'items' && <ItemMaster />}
            {activeTab === 'bom' && <BOMMaster />}
            {activeTab === 'itemmap' && <ItemMaterialMap />}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
